import React, { useMemo, useRef } from 'react';
import { graphql, useFragment } from 'react-relay';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { SORT_ASC } from '@atlassian/jira-native-issue-table/src/common/constants.tsx';
import type { async_issueViewCommonViews_AsyncConfigurableChildIssuesPanelUsingEntryPoint$key } from '@atlassian/jira-relay/src/__generated__/async_issueViewCommonViews_AsyncConfigurableChildIssuesPanelUsingEntryPoint.graphql';
import {
	ChildIssuesPanelSkeleton,
	MAX_ROWS as MAX_SKELETON_ROWS,
} from '@atlassian/jira-skeletons/src/ui/child-issues-panel/components/ChildIssuesPanelSkeleton.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { getChildIssuesLimit } from '@atlassian/jira-issue-view-common-constants/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	CHILD_ISSUES_PAGE_SIZE,
	SORT_OPTION,
} from '@atlassian/jira-issue-view-child-issues-panel/src/common/constants.tsx';
import { configurableChildIssuesPanelEntryPoint } from '../../entrypoint.tsx';
import { JQLBuilder, createConfigurableChildIssuesPanelQueryVariables } from '../utils/index.tsx';
import type { ConfigurableChildIssuesPanelProps } from './types.tsx';

const getSkeletonMaxRows = () => {
	// Prioritise the pagination FG since it will always max out to the page size
	if (fg('jira-issue-view-child-issues-panel-pagination')) {
		return CHILD_ISSUES_PAGE_SIZE;
	}

	// Then use the SSR FG
	if (fg('magma-3191-render-chin-skeleton-in-ssr')) {
		return getChildIssuesLimit();
	}

	return MAX_SKELETON_ROWS;
};

interface ConfigurableChildIssuesPanelEntryPointProps {
	rankFieldJql: string;
	hideDone: boolean;
	// TODO: Remove on jira-issue-view-child-issues-panel-merge-subtasks cleanup
	filterSubtasks: boolean | null;
}

const useConfigurableChildIssuesPanelEntryPoint = ({
	filterSubtasks,
	hideDone,
	rankFieldJql,
}: ConfigurableChildIssuesPanelEntryPointProps) => {
	const cloudId = useCloudId();
	const issueKey = useIssueKey();

	const entryPointParams = useMemo(
		() => ({
			variables: createConfigurableChildIssuesPanelQueryVariables({
				cloudId,
				jql: JQLBuilder({
					issueKey,
					filterSubtasks,
					hideDone: hideDone ? 'true' : null,
					sortColumn: rankFieldJql,
					sortDirection: SORT_ASC,
				}),
				fieldSetIds: [],
				issueKey,
			}),
		}),
		[cloudId, filterSubtasks, hideDone, issueKey, rankFieldJql],
	);
	const hasLoadedEntrypoint = useRef(false);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		configurableChildIssuesPanelEntryPoint,
		entryPointParams,
	);

	if (!hasLoadedEntrypoint.current) {
		entryPointActions.load();
		hasLoadedEntrypoint.current = true;
	}

	return { entryPointReferenceSubject };
};

const AsyncConfigurableChildIssuesPanelUsingEntryPoint = ({
	columnPickerRef,
	projectId,
	parentIssueTypeId,
	filterSubtasks,
	rootRelayFragment,
	totalChildIssueCount,
}: ConfigurableChildIssuesPanelProps) => {
	const data =
		useFragment<async_issueViewCommonViews_AsyncConfigurableChildIssuesPanelUsingEntryPoint$key>(
			graphql`
				fragment async_issueViewCommonViews_AsyncConfigurableChildIssuesPanelUsingEntryPoint on JiraQuery {
					userPreferences(cloudId: $cloudId) @optIn(to: "JiraUserPreferences") {
						isIssueViewHideDoneChildIssuesFilterEnabled
					}
					rankField(cloudId: $cloudId) {
						jqlTerm
					}
				}
			`,
			rootRelayFragment?.jira ?? null,
		);

	const rankFieldJql = data?.rankField?.jqlTerm || SORT_OPTION.CREATED;

	const isHideDoneFilterEnabled = Boolean(
		data?.userPreferences?.isIssueViewHideDoneChildIssuesFilterEnabled,
	);

	const runtimeProps = useMemo(
		() => ({
			columnPickerRef,
			filterSubtasks,
			projectId,
			parentIssueTypeId,
			rankFieldJql,
			rootRelayFragment,
			isHideDoneFilterEnabled,
		}),
		[
			columnPickerRef,
			filterSubtasks,
			projectId,
			parentIssueTypeId,
			rankFieldJql,
			rootRelayFragment,
			isHideDoneFilterEnabled,
		],
	);

	const { entryPointReferenceSubject } = useConfigurableChildIssuesPanelEntryPoint({
		filterSubtasks,
		rankFieldJql,
		hideDone: isHideDoneFilterEnabled,
	});

	return (
		<JiraEntryPointContainer
			id="AsyncConfigurableChildIssuesPanel"
			packageName="jiraIssueViewCommonViews"
			teamName="bento"
			entryPointReferenceSubject={entryPointReferenceSubject}
			fallback={
				<ChildIssuesPanelSkeleton rows={totalChildIssueCount} maxRows={getSkeletonMaxRows()} />
			}
			runtimeProps={runtimeProps}
		/>
	);
};

export default AsyncConfigurableChildIssuesPanelUsingEntryPoint;
